import React, { useEffect, useMemo, useState } from "react";
import { PlusCircleIcon, MinusCircleIcon } from "@heroicons/react/24/outline";
import { useAuthFetch } from "../../hooks/useAuthFetch";
import { Button } from "../../components/button";

const FREQUENCIES = [
  { value: "", label: "Once per shift" },
  { value: "1", label: "Every hour" },
  { value: "2", label: "Every 2 hours" },
  { value: "3", label: "Every 3 hours" },
  { value: "4", label: "Every 4 hours" },
  { value: "6", label: "Every 6 hours" },
  { value: "8", label: "Every 8 hours" },
];

// Same field style as the site form's emergency contacts.
const selectClass =
  "flex-1 min-w-0 rounded-md border-0 px-3 py-2.5 text-sm leading-none font-normal transition-colors duration-150 text-gray-500 dark:text-gray-200 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-white/[0.06] focus:outline-none";

const BLANK = { activity_type: null, activity_id: null, frequency_hours: null };
const key = (type, id) => `${type}:${id}`;

// Activities: the tours and checkpoints this post is responsible for, and how
// often. Rules only; scheduling turns them into timed activities on a shift.
// Site posts pick from their own site (siteId); patrol routes (siteId null)
// pick from any site. Rows without a tour/checkpoint chosen are dropped on save.
//
//   activities: [{ activity_type, activity_id, label, site, frequency_hours }]
function PostActivities({ siteId, activities, onChange }) {
  const authFetch = useAuthFetch();
  const [options, setOptions] = useState([]);

  useEffect(() => {
    authFetch(`${process.env.REACT_APP_API_URL}/v1/activities`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setOptions(Array.isArray(data) ? data : []))
      .catch((error) => console.error("Error fetching tours and checkpoints", error));
  }, [authFetch]);

  const choices = useMemo(
    () =>
      options
        .filter((o) => (siteId ? String(o.site?.id) === String(siteId) : true))
        .sort((a, b) =>
          `${a.site?.name}${a.type}${a.label}`.localeCompare(`${b.site?.name}${b.type}${b.label}`)
        ),
    [options, siteId]
  );

  const optionLabel = (o) =>
    [siteId ? null : o.site?.name, o.type === "tour" ? "Tour" : "Checkpoint", o.label]
      .filter(Boolean)
      .join(" · ");

  // Show one empty row to start, like the contacts list.
  const rows = activities.length ? activities : [BLANK];

  const update = (index, patch) =>
    onChange(rows.map((d, i) => (i === index ? { ...d, ...patch } : d)));

  const choose = (index, value) => {
    const o = choices.find((x) => key(x.type, x.id) === value);
    update(
      index,
      o
        ? { activity_type: o.type, activity_id: o.id, label: o.label, site: o.site }
        : { activity_type: null, activity_id: null, label: null, site: null }
    );
  };

  const remove = (index) => onChange(rows.filter((_, i) => i !== index));

  return (
    <fieldset>
      <legend className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-300">
        Activities
      </legend>
      {rows.map((d, index) => {
        const current = d.activity_id ? key(d.activity_type, d.activity_id) : "";
        // Each tour/checkpoint once per post: hide ones chosen on other rows.
        const takenElsewhere = new Set(
          rows
            .filter((_, i) => i !== index)
            .filter((r) => r.activity_id)
            .map((r) => key(r.activity_type, r.activity_id))
        );
        return (
          <div key={index} className="flex mt-2 items-center gap-2">
            <select
              aria-label="Tour or checkpoint"
              className={selectClass}
              value={current}
              onChange={(e) => choose(index, e.target.value)}
            >
              <option value="">Select tour or checkpoint</option>
              {choices
                .filter((o) => !takenElsewhere.has(key(o.type, o.id)))
                .map((o) => (
                  <option key={key(o.type, o.id)} value={key(o.type, o.id)}>
                    {optionLabel(o)}
                  </option>
                ))}
            </select>
            <select
              aria-label="How often"
              className={`${selectClass} max-w-[10rem]`}
              value={d.frequency_hours ? String(d.frequency_hours) : ""}
              onChange={(e) =>
                update(index, { frequency_hours: e.target.value ? Number(e.target.value) : null })
              }
            >
              {FREQUENCIES.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            <Button
              variant="secondary"
              icon={MinusCircleIcon}
              aria-label="Remove activity"
              onClick={() => remove(index)}
            />
          </div>
        );
      })}
      <Button
        variant="secondary"
        icon={PlusCircleIcon}
        aria-label="Add activity"
        onClick={() => onChange([...rows, BLANK])}
        className="mt-2"
      />
    </fieldset>
  );
}

export { PostActivities };
